/**
 * Klantpresentatie (.pptx) bij een offerte (2 nov 2026).
 *
 * De engine leest de definitieve offerte uit Gripp, laat de AI debriefing/aanpak/aannames
 * schrijven en zet de totalen per categorie erin, in de opmaak van onze presentaties.
 * Downloaden en zelf nalezen vóór het naar de klant gaat — er wordt niets verstuurd.
 */
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

export function base64NaarBlob(base64, type) {
  const binair = atob(base64);
  const bytes = new Uint8Array(binair.length);
  for (let i = 0; i < binair.length; i += 1) bytes[i] = binair.charCodeAt(i);
  return new Blob([bytes], { type });
}

export async function downloadOffertePresentatie({ offerNumber, source, senderName, companyName, afbeeldingen = [], videotheek = [] } = {}) {
  const nummer = String(offerNumber ?? '').trim();
  if (!/^\d+$/.test(nummer)) {
    toast.error('Maak de offerte eerst aan in Gripp — de presentatie leest de definitieve versie');
    return false;
  }
  const metBeelden = afbeeldingen.length + videotheek.length > 0;
  const toastId = toast.loading(metBeelden ? 'Presentatie met beelden maken… (± 45 sec)' : 'Presentatie maken… (± 30 sec)');
  try {
    const r = await authFetch(`${BACKEND_URL}/Api/offertes/presentatie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        offerNumber: nummer, source: source || '', senderName: senderName || '', companyName: companyName || '',
        afbeeldingen, videotheek,
      }),
    });
    let data = null;
    try { data = await r.json(); } catch { /* geen JSON */ }
    if (!r.ok || !data?.base64) {
      const detail = data?.detail;
      throw new Error(typeof detail === 'string' && detail ? detail : `Presentatie maken mislukt (${r.status})`);
    }
    const blob = base64NaarBlob(data.base64,
      'application/vnd.openxmlformats-officedocument.presentationml.presentation');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = data.filename || `Offerte ${nummer}.pptx`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    toast.success('Presentatie gedownload — lees hem na voordat hij naar de klant gaat', { id: toastId });
    return true;
  } catch (e) {
    toast.error(e?.message || 'Presentatie maken mislukt', { id: toastId });
    return false;
  }
}

/** Foto verkleinen in de browser (max 2000 px, JPEG) — een telefoonfoto van 12 MB wordt
 *  zo ± 400 kB en past ruim binnen de limiet van de engine. */
export function verkleinAfbeelding(bestand, maxPx = 2000) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(bestand);
    const img = new Image();
    img.onload = () => {
      const schaal = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * schaal);
      canvas.height = Math.round(img.height * schaal);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.86));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`${bestand.name} is geen afbeelding die de browser kan lezen`)); };
    img.src = url;
  });
}

export async function zoekVideotheek(q) {
  const r = await authFetch(`${BACKEND_URL}/Api/offertes/presentatie/videotheek?q=${encodeURIComponent(q || '')}`);
  if (!r.ok) throw new Error(`Videotheek zoeken mislukt (${r.status})`);
  return (await r.json()).videos || [];
}

/** Still als data-URL (de afbeelding vraagt een token, dus geen kale <img src>). Bewust geen
 *  blob-URL: die moet je zelf weer intrekken en lekte bij elk zoek-/framewissel (Codex 2 nov);
 *  een preview van 480 px is ± 30 kB en verdwijnt vanzelf met de state. */
export async function haalStill(videoId, seconde) {
  const t = seconde != null ? `?t=${encodeURIComponent(seconde)}` : '';
  const r = await authFetch(`${BACKEND_URL}/Api/offertes/presentatie/still/${videoId}${t}`);
  if (!r.ok) throw new Error('Geen still');
  const blob = await r.blob();
  return new Promise((resolve, reject) => {
    const lezer = new FileReader();
    lezer.onload = () => resolve(lezer.result);
    lezer.onerror = () => reject(new Error('Geen still'));
    lezer.readAsDataURL(blob);
  });
}
